import { useCallback, useContext } from 'react';

import { Button, Flex, Stack, VStack, useToast } from '@chakra-ui/react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuChevronRight } from 'react-icons/lu';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Form } from '@/components';
import { type CheckBoxOption } from '@/components/Form/FieldCreatableCheckBoxes';
import { Icon, IconAlertTriangle } from '@/components/Icons';
import {
  StepNewInterviewQuestions,
  useCreateInterviewMutation,
  useCreateTemplateInterviewMutation,
} from '@/features/admin-new-interview';
import {
  type InterviewRequest,
  InterviewType,
  QuestionType,
} from '@/services/api/codegen/api';

import { ADMIN_PATH } from '../admin/constants';
import { AlertToast } from '../video-recorder/useVideoRecorder';
import { InterviewTypeDescriptions } from './InterviewTypeDescriptions';
import { AdminNewInterviewContext } from './PageAdminNewInterview.ctx';
import { StepFinalizeNewInterview } from './StepFinalizeNewInterview';
import { StepInterviewInformation } from './StepInterviewInformation';
import { StepInterviewInstructions } from './StepInterviewInstructions';
import {
  type InterviewQuestionFormFields,
  type NewInterviewFormInputs,
  defaultQuestion,
} from './common';

const now = new Date();
const nextWeek = new Date(now);
nextWeek.setDate(nextWeek.getDate() + 7);

export const formatTime = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  let minutes = String(Math.round(date.getMinutes() / 15) * 15).padStart(
    2,
    '0'
  );

  if (minutes === '60') {
    minutes = '00';
  }

  return hours + ':' + minutes;
};

export const AdminNewInterviewContent = () => {
  const { t } = useTranslation(['common', 'interview', 'candidate']);
  const [searchParams] = useSearchParams();
  const isAdminTemplateInterview =
    searchParams.get('isAdminTemplate') === 'true';
  const navigate = useNavigate();

  const form = useForm<NewInterviewFormInputs>({
    mode: 'onBlur', // other validation options are slow for some reason
    reValidateMode: 'onBlur',
    defaultValues: {
      questions: [defaultQuestion],
      title: '',
      informationalText: '',
      startDate: now,
      startTime: formatTime(now),
      endDate: nextWeek,
      endTime: formatTime(now),
    },
  });

  const toast = useToast();

  const createInterviewMutation = useCreateInterviewMutation({
    onSuccess() {
      toast({
        title: t('interview:createNewInterview.successMessage'),
        status: 'success',
      });
    },
    onError() {
      toast({
        title: t('interview:createNewInterview.errorMessage'),
        status: 'error',
      });
    },
  });

  const createTemplateInterviewMutation = useCreateTemplateInterviewMutation({
    onSuccess() {
      toast({
        title: t('interview:createNewInterview.successTemplateMessage'),
        status: 'success',
      });
    },
    onError() {
      toast({
        title: t('interview:createNewInterview.errorMessage'),
        status: 'error',
      });
    },
  });

  const {
    activeStep,
    setActiveStep,
    goToNext,
    stepsCount,
    interviewId,
    jobDescription,
  } = useContext(AdminNewInterviewContext);
  const isSubmitStep = activeStep === stepsCount - 2;

  const ShouldCreateInterview = () => {
    const type = form.watch('type') || InterviewType.JobInterview;
    return isSubmitStep || type === InterviewType.InterviewAssistant;
  };

  const onSubmit: SubmitHandler<NewInterviewFormInputs> = useCallback(
    async (data) => {
      const isCreateInterviewStep = ShouldCreateInterview();

      if (isCreateInterviewStep) {
        // Create a default question for assistant interview type
        let questions = data.questions?.filter((x) => x.question != '') ?? [];

        if (
          data.type === InterviewType.InterviewAssistant &&
          questions.length === 0
        ) {
          const defaultAssistantQuestion: InterviewQuestionFormFields = {
            question: t(
              'interview:createNewInterview.questionPreparation.interviewAssistantInterviewType'
            ),
            questionType: QuestionType.Video,
            durationMinutes: 5,
          };
          questions = [defaultAssistantQuestion];
        }

        const interviewRequest: InterviewRequest = {
          title: data.title?.trim(),
          startDate: mergeDateAndTime(data.startDate, data.startTime),
          endDate: mergeDateAndTime(data.endDate, data.endTime),
          informationalText: data.informationalText?.trim(),
          detail: data.informationalText?.trim(),
          jobDescription: jobDescription.current ?? '',
          language: data.language,
          interviewSettings: data.interviewSettings,
          questions: questions?.map((x) => {
            const isCaseMode = x.isCaseMode;
            const question = isCaseMode ? x.questionMarkdown : x.question;
            const other = (x?.choices as Array<CheckBoxOption>)?.find(
              (c) => c.value === 'other'
            );
            let filteredChoices = x?.choices as Array<CheckBoxOption>;
            if (other) {
              x.settings = { ...x.settings, hasOtherValue: true };
              filteredChoices = filteredChoices.filter(
                (c) => c?.value !== 'other'
              );
            }
            const choices = filteredChoices?.map((c) => ({
              isCorrect: c.checked,
              title: c.label,
            }));

            const isFileAudio = x.file?.type?.includes('audio');

            return {
              question: question?.trim(),
              durationMinutes: x.durationMinutes
                ? x.durationMinutes
                : undefined,
              isCaseMode,
              skill: x.skill,
              settings: x.settings ?? undefined,
              questionType: x.questionType,
              tags: x.tags,
              choices: choices,
              mediaFileKeys: isFileAudio ? x.mediaFileKeys : [],
              imageFileKeys: isFileAudio ? [] : x.imageFileKeys,
              programmingLanguage: x.programmingLanguage,
            };
          }),
          type: data.type,
        };

        if (isAdminTemplateInterview) {
          interviewId.current =
            await createTemplateInterviewMutation.mutateAsync(interviewRequest);
          navigate(`${ADMIN_PATH}/interviews?tab=templates`);
          return;
        }

        interviewId.current =
          await createInterviewMutation.mutateAsync(interviewRequest);
      }

      if (data.type === InterviewType.InterviewAssistant) {
        // move to last step
        setActiveStep(3);
        return;
      }

      goToNext();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeStep, interviewId, jobDescription, stepsCount]
  );

  const checkQuestions = useCallback(() => {
    form.getValues('questions')?.forEach((question) => {
      if (question.questionType === QuestionType.MultipleChoice) {
        const isChoiceSelected = question.choices?.some(
          (c) => (c as CheckBoxOption).checked
        );
        if (!isChoiceSelected) {
          toast({
            render: (props) => (
              <AlertToast
                {...props}
                message={t(
                  'interview:createNewInterview.selectAnOptionBeforeProceeding'
                )}
              />
            ),
            status: 'warning',
            icon: <IconAlertTriangle />,
            id: 'unique-toast',
            variant: 'subtle',
            isClosable: true,
          });
        }
      }
    });
  }, [form, t, toast]);

  return (
    <Stack spacing={5}>
      <Form {...form} onSubmit={onSubmit}>
        <VStack
          spacing={4}
          align="stretch"
          border="1px"
          borderRadius="xl"
          borderColor="gray.50"
          bg="white"
          p={6}
        >
          <StepInterviewInformation isActive={activeStep === 0} />
          <StepInterviewInstructions isActive={activeStep === 1} />
          <StepNewInterviewQuestions isActive={activeStep === 2} />
          <StepFinalizeNewInterview
            isActive={activeStep === 3}
            onNew={() => {
              window.location.reload();
            }}
          />
          <Flex mt={4} justify="end">
            {activeStep === stepsCount - 1 ? null : (
              <Button
                type="submit"
                isLoading={
                  createInterviewMutation.isPending ||
                  createTemplateInterviewMutation.isPending
                }
                onClick={checkQuestions}
                variant="@primary"
                rightIcon={<Icon icon={LuChevronRight} fontSize="xl" />}
              >
                {ShouldCreateInterview()
                  ? t('interview:createNewInterview.createInterview')
                  : t('common:actions.continue')}
              </Button>
            )}
          </Flex>
        </VStack>
      </Form>
      {activeStep === 0 && <InterviewTypeDescriptions />}
    </Stack>
  );
};

export function mergeDateAndTime(date: Date, time: string) {
  // Split the time string to get hours and minutes
  const [hours, minutes] = time.split(':').map(Number);

  // Set hours and minutes on the date
  date.setHours(hours!);
  date.setMinutes(minutes!);
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date.toISOString();
}
